import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { rfqAPI, quotationAPI, poAPI, invoiceAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AdminSidebar } from '../components/admin/AdminSidebar';
import { formatDate } from '../utils/formatDate';
import { toast } from 'sonner';
import {
  ShoppingCart, FileCheck, Package, Receipt, Plus, Eye, Check, X,
  LogOut, Loader2, Trash2, Send
} from 'lucide-react';

const RFQ_STATUS = {
  open: { label: 'Open', color: 'bg-emerald-100 text-emerald-700' },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' },
};

const QUOTATION_STATUS = {
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-700' },
  under_review: { label: 'Under Review', color: 'bg-amber-100 text-amber-700' },
  accepted: { label: 'Accepted', color: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
};

const PO_STATUS = {
  issued: { label: 'Issued', color: 'bg-blue-100 text-blue-700' },
  acknowledged: { label: 'Acknowledged', color: 'bg-purple-100 text-purple-700' },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  delivered: { label: 'Delivered', color: 'bg-emerald-100 text-emerald-700' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700' },
};

const INVOICE_STATUS = {
  submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-700' },
  verified: { label: 'Verified', color: 'bg-purple-100 text-purple-700' },
  payment_initiated: { label: 'Payment Initiated', color: 'bg-amber-100 text-amber-700' },
  paid: { label: 'Paid', color: 'bg-emerald-100 text-emerald-700' },
};

export default function StoresManagement() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rfqs, setRFQs] = useState([]);
  const [pos, setPOs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState('rfqs');

  // Modal states
  const [showCreateRFQ, setShowCreateRFQ] = useState(false);
  const [showRFQDetail, setShowRFQDetail] = useState(false);
  const [showPODetail, setShowPODetail] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedRFQ, setSelectedRFQ] = useState(null);
  const [selectedPO, setSelectedPO] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [rfqQuotations, setRFQQuotations] = useState([]);
  const [poInvoices, setPOInvoices] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  // RFQ Form
  const [rfqForm, setRFQForm] = useState({
    title: '',
    description: '',
    category: 'goods',
    items: [{ item_name: '', description: '', quantity: 1, unit: 'nos', specifications: '' }],
    submission_deadline: '',
    delivery_location: '',
    terms_conditions: ''
  });

  // Delivery Form
  const [deliveryForm, setDeliveryForm] = useState({
    delivery_status: 'pending',
    delivery_date_actual: '',
    delivery_remarks: ''
  });

  // Payment Form
  const [paymentForm, setPaymentForm] = useState({
    payment_status: 'pending',
    payment_date: '',
    payment_reference: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rfqRes, poRes, invRes] = await Promise.all([
        rfqAPI.getRFQs(),
        poAPI.getPOs(),
        invoiceAPI.getInvoices(),
      ]);
      setRFQs(rfqRes.data || []);
      setPOs(poRes.data || []);
      setInvoices(invRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const addRFQItem = () => {
    setRFQForm({
      ...rfqForm,
      items: [...rfqForm.items, { item_name: '', description: '', quantity: 1, unit: 'nos', specifications: '' }]
    });
  };

  const removeRFQItem = (index) => {
    setRFQForm({
      ...rfqForm,
      items: rfqForm.items.filter((_, i) => i !== index)
    });
  };

  const updateRFQItem = (index, field, value) => {
    const newItems = [...rfqForm.items];
    newItems[index][field] = value;
    setRFQForm({ ...rfqForm, items: newItems });
  };

  const createRFQ = async () => {
    if (!rfqForm.title || !rfqForm.submission_deadline || rfqForm.items.some(i => !i.item_name)) {
      toast.error('Please fill all required fields');
      return;
    }

    setActionLoading(true);
    try {
      await rfqAPI.createRFQ(rfqForm);
      toast.success('RFQ created successfully');
      setShowCreateRFQ(false);
      setRFQForm({
        title: '',
        description: '',
        category: 'goods',
        items: [{ item_name: '', description: '', quantity: 1, unit: 'nos', specifications: '' }],
        submission_deadline: '',
        delivery_location: '',
        terms_conditions: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create RFQ');
    } finally {
      setActionLoading(false);
    }
  };

  const viewRFQDetail = async (rfq) => {
    setSelectedRFQ(rfq);
    try {
      const response = await rfqAPI.getRFQDetail(rfq.id);
      setRFQQuotations(response.data.quotations || []);
      setShowRFQDetail(true);
    } catch (error) {
      toast.error('Failed to fetch RFQ details');
    }
  };

  const acceptQuotation = async (quotationId) => {
    setActionLoading(true);
    try {
      await quotationAPI.acceptQuotation(quotationId);
      toast.success('Quotation accepted! RFQ closed.');
      setShowRFQDetail(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to accept quotation');
    } finally {
      setActionLoading(false);
    }
  };

  const closeRFQ = async (rfqId) => {
    try {
      await rfqAPI.closeRFQ(rfqId);
      toast.success('RFQ closed');
      fetchData();
    } catch (error) {
      toast.error('Failed to close RFQ');
    }
  };

  const viewPODetail = async (po) => {
    setSelectedPO(po);
    try {
      const response = await poAPI.getPODetail(po.id);
      setPOInvoices(response.data.invoices || []);
      setShowPODetail(true);
    } catch (error) {
      toast.error('Failed to fetch PO details');
    }
  };

  const openDeliveryModal = (po) => {
    setSelectedPO(po);
    setDeliveryForm({
      delivery_status: po.delivery_status || 'pending',
      delivery_date_actual: po.delivery_date_actual || '',
      delivery_remarks: po.delivery_remarks || ''
    });
    setShowDeliveryModal(true);
  };

  const updateDelivery = async () => {
    setActionLoading(true);
    try {
      await poAPI.updateDelivery(selectedPO.id, deliveryForm);
      toast.success('Delivery status updated');
      setShowDeliveryModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update delivery');
    } finally {
      setActionLoading(false);
    }
  };

  const openPaymentModal = (po) => {
    setSelectedPO(po);
    setPaymentForm({
      payment_status: po.payment_status || 'pending',
      payment_date: po.payment_date || '',
      payment_reference: po.payment_reference || ''
    });
    setShowPaymentModal(true);
  };

  const updatePayment = async () => {
    setActionLoading(true);
    try {
      await poAPI.updatePayment(selectedPO.id, paymentForm);
      toast.success('Payment status updated');
      setShowPaymentModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update payment');
    } finally {
      setActionLoading(false);
    }
  };

  const verifyInvoice = async (invoiceId) => {
    try {
      await invoiceAPI.verifyInvoice(invoiceId);
      toast.success('Invoice verified');
      fetchData();
    } catch (error) {
      toast.error('Failed to verify invoice');
    }
  };

  const markInvoicePaid = async (invoiceId) => {
    try {
      await invoiceAPI.updatePayment(invoiceId, 'paid', `PAY-${Date.now()}`);
      toast.success('Invoice marked as paid');
      fetchData();
    } catch (error) {
      toast.error('Failed to update payment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#003380]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="tricolor-strip" />
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/iith-logo.png" 
                alt="IIT Hyderabad" 
                className="h-12 w-auto"
              />
              <div>
                <h1 className="text-xl font-bold govt-blue-text font-serif">Stores & Procurement</h1>
                <p className="text-sm text-slate-600">RFQ, PO & Invoice Management</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">
                <span className="font-medium">{user?.name}</span>
                <span className="text-slate-400 mx-2">|</span>
                <span className="capitalize">{user?.role?.replace('_', ' ')}</span>
              </span>
              <Button variant="outline" onClick={handleLogout} size="sm">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        <AdminSidebar />

        {/* Main Content */}
        <main className="flex-1 p-6">
          {/* Vendor Registration Approval Channel Info */}
          <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-[#003380]">Vendor Registration Approval Channel</h3>
                  <p className="text-sm text-slate-600 mt-1">Applications follow this approval workflow:</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-lg shadow-sm">
                    <div className="text-center">
                      <div className="font-bold text-[#003380]">DA</div>
                      <div className="text-xs text-slate-500">Dealing Asst.</div>
                    </div>
                    <div className="text-2xl text-slate-400">→</div>
                    <div className="text-center">
                      <div className="font-bold text-[#003380]">AR</div>
                      <div className="text-xs text-slate-500">Asst. Registrar</div>
                    </div>
                    <div className="text-2xl text-slate-400">→</div>
                    <div className="text-center">
                      <div className="font-bold text-emerald-600">HoS</div>
                      <div className="text-xs text-slate-500">Head of Stores</div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <div className="flex items-center justify-between">
              <TabsList className="bg-white border">
                <TabsTrigger value="rfqs">
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  RFQs ({rfqs.length})
                </TabsTrigger>
                <TabsTrigger value="pos">
                  <Package className="h-4 w-4 mr-2" />
                  Purchase Orders ({pos.length})
                </TabsTrigger>
                <TabsTrigger value="invoices">
                  <Receipt className="h-4 w-4 mr-2" />
                  Invoices ({invoices.length})
                </TabsTrigger>
              </TabsList>

              {activeTab === 'rfqs' && (
                <Button onClick={() => setShowCreateRFQ(true)} className="govt-btn-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Create RFQ
                </Button>
              )}
            </div>

            {/* RFQs Tab */}
            <TabsContent value="rfqs">
              <Card className="govt-card">
                <CardHeader className="govt-card-header">
                  <CardTitle>Request for Quotations</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {rfqs.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <ShoppingCart className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>No RFQs created yet</p>
                    </div>
                  ) : (
                    <table className="govt-table">
                      <thead>
                        <tr>
                          <th>RFQ Number</th>
                          <th>Title</th>
                          <th>Category</th>
                          <th>Deadline</th>
                          <th>Quotations</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rfqs.map((rfq) => (
                          <tr key={rfq.id}>
                            <td className="font-medium">{rfq.rfq_number}</td>
                            <td>{rfq.title}</td>
                            <td className="capitalize">{rfq.category}</td>
                            <td>{formatDate(rfq.submission_deadline)}</td>
                            <td>
                              <Badge variant="outline">{rfq.quotations_count || 0} received</Badge>
                            </td>
                            <td>
                              <Badge className={RFQ_STATUS[rfq.status]?.color}>
                                {RFQ_STATUS[rfq.status]?.label}
                              </Badge>
                            </td>
                            <td>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => viewRFQDetail(rfq)}>
                                  <Eye className="h-4 w-4" />
                                </Button>
                                {rfq.status === 'open' && (
                                  <Button size="sm" variant="outline" onClick={() => closeRFQ(rfq.id)}>
                                    <X className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* POs Tab */}
            <TabsContent value="pos">
              <Card className="govt-card">
                <CardHeader className="govt-card-header">
                  <CardTitle>Purchase Orders</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {pos.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>No purchase orders yet</p>
                    </div>
                  ) : (
                    <table className="govt-table">
                      <thead>
                        <tr>
                          <th>PO Number</th>
                          <th>Vendor</th>
                          <th>Amount</th>
                          <th>Delivery Date</th>
                          <th>Delivery Status</th>
                          <th>Payment</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pos.map((po) => (
                          <tr key={po.id}>
                            <td className="font-medium">{po.po_number}</td>
                            <td>{po.vendor_name}</td>
                            <td>₹{po.total_amount?.toLocaleString('en-IN')}</td>
                            <td>{formatDate(po.delivery_date)}</td>
                            <td className="capitalize">{po.delivery_status || 'Pending'}</td>
                            <td className="capitalize">{po.payment_status || 'Pending'}</td>
                            <td>
                              <Badge className={PO_STATUS[po.status]?.color}>
                                {PO_STATUS[po.status]?.label}
                              </Badge>
                            </td>
                            <td>
                              <div className="flex gap-1">
                                <Button size="sm" variant="outline" onClick={() => viewPODetail(po)} title="View">
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => openDeliveryModal(po)} title="Update Delivery">
                                  <Package className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => openPaymentModal(po)} title="Update Payment">
                                  <Receipt className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Invoices Tab */}
            <TabsContent value="invoices">
              <Card className="govt-card">
                <CardHeader className="govt-card-header">
                  <CardTitle>Invoices</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {invoices.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Receipt className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>No invoices submitted yet</p>
                    </div>
                  ) : (
                    <table className="govt-table">
                      <thead>
                        <tr>
                          <th>Invoice #</th>
                          <th>PO #</th>
                          <th>Vendor</th>
                          <th>Date</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((inv) => (
                          <tr key={inv.id}>
                            <td className="font-medium">{inv.invoice_number}</td>
                            <td>{inv.po_number}</td>
                            <td>{inv.vendor_name}</td>
                            <td>{formatDate(inv.invoice_date)}</td>
                            <td>₹{inv.total_amount?.toLocaleString('en-IN')}</td>
                            <td>
                              <Badge className={INVOICE_STATUS[inv.status]?.color}>
                                {INVOICE_STATUS[inv.status]?.label}
                              </Badge>
                            </td>
                            <td>
                              <div className="flex gap-1">
                                {inv.status === 'submitted' && (
                                  <Button size="sm" onClick={() => verifyInvoice(inv.id)}>
                                    <Check className="h-4 w-4 mr-1" />
                                    Verify
                                  </Button>
                                )}
                                {inv.status === 'verified' && (
                                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => markInvoicePaid(inv.id)}>
                                    <Check className="h-4 w-4 mr-1" />
                                    Mark Paid
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </main>
      </div>

      {/* Create RFQ Modal */}
      <Dialog open={showCreateRFQ} onOpenChange={setShowCreateRFQ}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Request for Quotation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Title *</Label>
                <Input
                  value={rfqForm.title}
                  onChange={(e) => setRFQForm({ ...rfqForm, title: e.target.value })}
                  placeholder="e.g., Supply of Laboratory Equipment"
                />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={rfqForm.description}
                  onChange={(e) => setRFQForm({ ...rfqForm, description: e.target.value })}
                  placeholder="Detailed description of requirements..."
                />
              </div>
              <div>
                <Label>Category *</Label>
                <Select value={rfqForm.category} onValueChange={(v) => setRFQForm({ ...rfqForm, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="goods">Goods</SelectItem>
                    <SelectItem value="services">Services</SelectItem>
                    <SelectItem value="works">Works</SelectItem>
                    <SelectItem value="consultancy">Consultancy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Submission Deadline *</Label>
                <Input
                  type="date"
                  value={rfqForm.submission_deadline}
                  onChange={(e) => setRFQForm({ ...rfqForm, submission_deadline: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Items *</Label>
                <Button type="button" size="sm" variant="outline" onClick={addRFQItem}>
                  <Plus className="h-4 w-4 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-2">
                {rfqForm.items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end border p-2 rounded">
                    <div className="col-span-4">
                      <Label className="text-xs">Item Name</Label>
                      <Input
                        value={item.item_name}
                        onChange={(e) => updateRFQItem(idx, 'item_name', e.target.value)}
                        placeholder="Item name"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Quantity</Label>
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateRFQItem(idx, 'quantity', parseInt(e.target.value))}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Unit</Label>
                      <Input
                        value={item.unit}
                        onChange={(e) => updateRFQItem(idx, 'unit', e.target.value)}
                        placeholder="nos/kg/etc"
                      />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Specifications</Label>
                      <Input
                        value={item.specifications}
                        onChange={(e) => updateRFQItem(idx, 'specifications', e.target.value)}
                        placeholder="Specs"
                      />
                    </div>
                    <div className="col-span-1">
                      {rfqForm.items.length > 1 && (
                        <Button type="button" size="sm" variant="ghost" onClick={() => removeRFQItem(idx)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Delivery Location</Label>
              <Input
                value={rfqForm.delivery_location}
                onChange={(e) => setRFQForm({ ...rfqForm, delivery_location: e.target.value })}
                placeholder="e.g., IIT Hyderabad, Chemistry Department"
              />
            </div>
            <div>
              <Label>Terms & Conditions</Label>
              <Textarea
                value={rfqForm.terms_conditions}
                onChange={(e) => setRFQForm({ ...rfqForm, terms_conditions: e.target.value })}
                placeholder="Payment terms, delivery terms, etc."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateRFQ(false)}>Cancel</Button>
            <Button onClick={createRFQ} disabled={actionLoading} className="govt-btn-primary">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Publish RFQ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RFQ Detail Modal */}
      <Dialog open={showRFQDetail} onOpenChange={setShowRFQDetail}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedRFQ?.title}</DialogTitle>
            <DialogDescription>{selectedRFQ?.rfq_number}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Category:</span>
                <span className="ml-2 capitalize">{selectedRFQ?.category}</span>
              </div>
              <div>
                <span className="text-slate-500">Deadline:</span>
                <span className="ml-2">{formatDate(selectedRFQ?.submission_deadline)}</span>
              </div>
              <div>
                <Badge className={RFQ_STATUS[selectedRFQ?.status]?.color}>
                  {RFQ_STATUS[selectedRFQ?.status]?.label}
                </Badge>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Items</h4>
              <table className="w-full text-sm border">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-2">Item</th>
                    <th className="text-left p-2">Qty</th>
                    <th className="text-left p-2">Unit</th>
                    <th className="text-left p-2">Specifications</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedRFQ?.items?.map((item, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="p-2">{item.item_name}</td>
                      <td className="p-2">{item.quantity}</td>
                      <td className="p-2">{item.unit}</td>
                      <td className="p-2">{item.specifications}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Quotations Received ({rfqQuotations.length})</h4>
              {rfqQuotations.length === 0 ? (
                <p className="text-slate-500 text-sm">No quotations received yet</p>
              ) : (
                <div className="space-y-3">
                  {rfqQuotations.map((q) => (
                    <div key={q.id} className="border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="font-medium">{q.vendor_name}</span>
                          <span className="text-sm text-slate-500 ml-2">({q.quotation_number})</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-lg">₹{q.total_amount?.toLocaleString('en-IN')}</span>
                          <Badge className={QUOTATION_STATUS[q.status]?.color}>
                            {QUOTATION_STATUS[q.status]?.label}
                          </Badge>
                        </div>
                      </div>
                      <div className="text-sm text-slate-600">
                        <span>Validity: {q.validity_days} days</span>
                        {q.delivery_terms && <span className="ml-4">Delivery: {q.delivery_terms}</span>}
                      </div>
                      {q.status === 'submitted' && selectedRFQ?.status === 'open' && (
                        <div className="mt-2">
                          <Button size="sm" onClick={() => acceptQuotation(q.id)} disabled={actionLoading}>
                            <Check className="h-4 w-4 mr-1" />
                            Accept Quotation
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delivery Update Modal */}
      <Dialog open={showDeliveryModal} onOpenChange={setShowDeliveryModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Delivery Status</DialogTitle>
            <DialogDescription>PO: {selectedPO?.po_number}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Delivery Status</Label>
              <Select value={deliveryForm.delivery_status} onValueChange={(v) => setDeliveryForm({ ...deliveryForm, delivery_status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="dispatched">Dispatched</SelectItem>
                  <SelectItem value="in_transit">In Transit</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Actual Delivery Date</Label>
              <Input
                type="date"
                value={deliveryForm.delivery_date_actual}
                onChange={(e) => setDeliveryForm({ ...deliveryForm, delivery_date_actual: e.target.value })}
              />
            </div>
            <div>
              <Label>Remarks (GRN Notes)</Label>
              <Textarea
                value={deliveryForm.delivery_remarks}
                onChange={(e) => setDeliveryForm({ ...deliveryForm, delivery_remarks: e.target.value })}
                placeholder="Inspection notes, condition of goods, etc."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeliveryModal(false)}>Cancel</Button>
            <Button onClick={updateDelivery} disabled={actionLoading} className="govt-btn-primary">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Update Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Payment Status</DialogTitle>
            <DialogDescription>PO: {selectedPO?.po_number}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Payment Status</Label>
              <Select value={paymentForm.payment_status} onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="initiated">Payment Initiated</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Payment Reference (UTR/Transaction ID)</Label>
              <Input
                value={paymentForm.payment_reference}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_reference: e.target.value })}
                placeholder="e.g., UTR123456789"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentModal(false)}>Cancel</Button>
            <Button onClick={updatePayment} disabled={actionLoading} className="govt-btn-primary">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
